import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnalyticsEvent } from '@app/core/services/analytics/analytics.events';
import { AnalyticsService } from '@app/core/services/analytics/analytics.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ChangeReviewDialogComponent } from './change-review-dialog/change-review-dialog.component';
import { diffChanges } from './change-review.utils';
import {
  ChangeEntry,
  ChangeKind,
  ChangeReviewConfig,
  ChangeReviewDialogData,
  ChangeReviewOptions,
  ChangeReviewResult,
} from './change-review.model';

@Injectable({ providedIn: 'root' })
export class ChangeReviewService {
  constructor(
    private dialog: MatDialog,
    private toastService: ToastService,
    private analytics: AnalyticsService,
  ) {}

  // after is a function: the review is recomputed after each revert
  review(
    before: unknown,
    after: () => unknown,
    config: ChangeReviewConfig,
    options: ChangeReviewOptions = {},
  ): Observable<ChangeReviewResult> {
    const diff = () => diffChanges(before, after(), config);
    const changes = diff();

    if (!changes.length) {
      this.toastService.showToast({ message: 'No changes to save', mode: 'info' });
      this.trackReview('noChanges', changes);
      return of('noChanges');
    }

    const { revert } = options;
    // a revert that leaves its change in place would duplicate data on every click
    const stuck = new Set<string>();
    const keyOf = (entry: ChangeEntry) => JSON.stringify([entry.path, entry.kind, entry.before, entry.after]);
    const data: ChangeReviewDialogData = {
      subtitle: config.subtitle,
      changes,
      stale: options.stale,
      onViewAuditLogs: options.onViewAuditLogs,
      canRevert: (entry) => !stuck.has(keyOf(entry)) && !!revert?.(entry),
      revert: (entries) => {
        const undos = entries.map((entry) => revert?.(entry)).filter(Boolean);
        const putsBack = entries.every((entry) => entry.kind === ChangeKind.Removed);
        (putsBack ? undos : undos.reverse()).forEach((undo) => undo());
        const remaining = diff();
        const remainingKeys = new Set(remaining.map(keyOf));
        entries.filter((entry) => remainingKeys.has(keyOf(entry))).forEach((entry) => stuck.add(keyOf(entry)));
        return remaining;
      },
    };

    return this.dialog
      .open<ChangeReviewDialogComponent, ChangeReviewDialogData, boolean>(ChangeReviewDialogComponent, {
        data,
        disableClose: true,
        autoFocus: 'first-heading',
        position: { top: '8vh' },
      })
      .afterClosed()
      .pipe(
        map((confirmed): ChangeReviewResult => (confirmed ? 'confirmed' : 'cancelled')),
        tap((result) => this.trackReview(result, result === 'confirmed' ? diff() : changes)),
      );
  }

  private trackReview(result: ChangeReviewResult, changes: ChangeEntry[]): void {
    const flagged = changes.filter((change) => change.level !== 'light');
    this.analytics.capture(AnalyticsEvent.ChangesReviewed, {
      routePattern: this.analytics.currentRoutePattern,
      result,
      changeCount: changes.length,
      riskyCount: flagged.length,
      riskyFields: [...new Set(flagged.map((change) => change.pattern))],
    });
  }
}
